import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Dialog, DialogContent, DialogTitle, Divider, Link, Grid, IconButton, Typography, DialogActions } from '@mui/material';
import {NOTIFICATION_TYPE, showNotificationBar} from '../../../actions/error-pages/NotificationBarActions';
import { getUserId, isAdmin, isUserAdmin } from '../../../actions/profile/UserActions';
import Colors from '../../../constants/Colors';
import { ContainedButton, LoadingButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { updateSiteSubmission } from '../../../actions/internal/SiteActions';
import { formatDateTimeToFancyString } from '../../../utilities/DateUtilities';
import { SUBMISSION_STATUS, SUBMISSION_STATUS_LOOKUP, SUBMISSION_TYPES_HUMAN_MAP } from '../../../constants/Statuses';
import StyledLineItem from '../../common/StyledLineItem';
import ConfirmationModal from './ConfirmationModal';
import EditRequestModal from './EditRequestModal';
import { formatClockifyTimeInterval } from '../../../utilities/DateUtilities';
import StyledDataTable from '../../common/StyledDataTable';
import StyledDialog from '../../common/StyledDialog';
import { getAllSubmissionsForAdmin, getAllTeamMembersForAdmin, getCurrentClockifyTimerForAdmin, toggleClockifyTimerForAdmin } from '../../../actions/internal/AdminActions';
import { LaunchOutlined, Pause, PauseCircle, PlayArrow, PlayArrowOutlined, StopCircle } from '@mui/icons-material';
import { getAllTeamMembers, getCurrentTimer } from '../../../selectors/AdminSelectors';
import { StyledAccordionItem } from '../../common/StyledAccordion';
import { SUMMARY_TITLE_FONT } from '../../../constants/Styles';
import { StyledTextField } from '../../common/StyledTextField';
import { StyledDatePicker } from '../../common/StyledDatePicker';
import dayjs from 'dayjs';
import AddTimeEntryModal from './AddTimeEntryModal';

const ViewRequestModal = ({ open, onClose, request, match, timer }) => {

  const timeEntry = useRef({});

  const [ confirmCancelRequest, setConfirmCancelRequest ] = useState(false);
  const [ showEditRequestModal, setShowEditRequestModal ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isLoadingMembers, setIsLoadingMembers] = useState(false);

  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const [showTimerSummaryDialog, setShowTimerSummaryDialog] = useState(false);
  const [timerStopTime, setTimerStopTime] = useState(null);

  const teamMembers = getAllTeamMembers();

  const requestSubmissionId = request && request.submission_id;
  const timerSubmissionId = timer && timer.submission_id;
  const timerId = timer && timer.id;

  const timerStartValue =
    timer &&
    timer.timeInterval &&
    timer.timeInterval.start
      ? timer.timeInterval.start
      : timer && timer.startTime;

  useEffect(() => { loadData(); }, [])

  const loadData = async () => {
    try {
      setIsLoadingMembers(true);
      if (isAdmin()) {
        await getAllTeamMembersForAdmin(getUserId());
      }
    } catch (ex) {
      const err = await ex;
      console.error(err);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "Unable to load team member information at this time.");
    } finally {
      setIsLoadingMembers(false);
    }
  }

  useEffect(() => {
    const hasMatchingTimer =
      requestSubmissionId &&
      timerSubmissionId &&
      requestSubmissionId === timerSubmissionId &&
      timerStartValue;
  
    if (!hasMatchingTimer) {
      setIsTimerRunning(false);
      setTimerStartTime(null);
      setTimerStopTime(null);
      setElapsedSeconds(0);
      return;
    }
  
    const startTime = new Date(timerStartValue).getTime();
  
    if (Number.isNaN(startTime)) {
      setIsTimerRunning(false);
      setTimerStartTime(null);
      setElapsedSeconds(0);
      return;
    }
  
    setIsTimerRunning(true);
    setTimerStartTime(timerStartValue);
  
    const seconds = Math.floor((Date.now() - startTime) / 1000);
    setElapsedSeconds(Math.max(0, seconds));
  }, [requestSubmissionId, timerSubmissionId, timerId, timerStartValue]);

  useEffect(() => {
    if (!isTimerRunning || !timerStartTime) return;
  
    const updateElapsedSeconds = () => {
      const startTime = new Date(timerStartTime).getTime();
  
      if (Number.isNaN(startTime)) {
        setElapsedSeconds(0);
        return;
      }
  
      const seconds = Math.floor((Date.now() - startTime) / 1000);
      setElapsedSeconds(Math.max(0, seconds));
    };
  
    updateElapsedSeconds();
  
    const interval = setInterval(updateElapsedSeconds, 1000);
  
    return () => clearInterval(interval);
  }, [isTimerRunning, timerStartTime]);
  
  const formattedElapsedTime = useMemo(() => {
    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const seconds = elapsedSeconds % 60;
  
    return [hours, minutes, seconds]
      .map((value) => String(value).padStart(2, '0'))
      .join(':');
  }, [elapsedSeconds]);
  
  const handleStartTimer = async () => {
    try {
      const startedAt = dayjs();

      setTimerStartTime(startedAt);
      setElapsedSeconds(0);
      setIsTimerRunning(true);
  
      await toggleClockifyTimerForAdmin({
        user_id: getUserId(),
        submission_id: request.submission_id
      });
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "Timer started.");
    } catch (ex) {
      const err = await ex;
      console.error(err);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "Timer was not able to be started. Please refresh and try again.");
    }
  };
  
  const handlePauseTimer = async () => {
    // Replace with your actual pause API call
    // Example: await pauseTimerApi(submission_id);
    const stoppedAt = dayjs();

    timeEntry.current = { startTime: timerStartTime, stopTime: stoppedAt, clockifyTimer: timer };

    setTimerStopTime(stoppedAt);
    setIsTimerRunning(false);
    setShowTimerSummaryDialog(true);
  };

  const handleClose = () => {
    onClose();
  }

  const handleCLoseTimerEntry = () => {
    setIsTimerRunning(true);
    setShowTimerSummaryDialog(false);
  }

  const handleDiscardTimeEntry = () => {
    setIsTimerRunning(false);
    setShowTimerSummaryDialog(false);
  }

  const handleSaveTimeEntry = (timer) => {
    setIsTimerRunning(false);
    setTimerStartTime(null);
    setTimerStopTime(null);
    setElapsedSeconds(0);
    setShowTimerSummaryDialog(false);
  }

  const handleCancelRequest = async () => {
    try {
      setIsLoading(true)
      setConfirmCancelRequest(false)
      const site_id = request.submission_identifiers.site ? request.submission_identifiers.site.site_id : undefined;
      await updateSiteSubmission(getUserId(), site_id, request.submission_id, ['submission_status'], { submission_status: SUBMISSION_STATUS.CANCELED });
      if (isAdmin()) {
        await getAllSubmissionsForAdmin(getUserId(), true, true);
      }
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "Request canceled.");
      onClose();
    } catch(ex) {
      console.error(ex);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "An error has occurred while canceling this request. If this continues to happen, please contact us.");
    } finally {
      setIsLoading(false)
    }
  }

  const getTeamMemberFormattedName = (
    teamMembers,
    user_id,
  ) => {
    if (!teamMembers || !user_id) return undefined;
  
    const mem = teamMembers.find(member => member.user_id === user_id);
    if (!mem) return undefined;

    return `${mem.team_member_details.first_name} ${mem.team_member_details.last_name}`;
  };

  const { submission_received_epoch, submission_type, submission_status, submission_assigned_to, submission_id, submission_fields, submission_attachments, submission_hours, submission_identifiers } = request || {}

  const isClosedSubmission = () => submission_status == SUBMISSION_STATUS.COMPLETED || submission_status == SUBMISSION_STATUS.CANCELED;

  const getFormattedRowsOfHours = (time_entries) => {
    if (!time_entries) return [];
    let hourRows = [];
    let totalDurationSec = 0;
    const rows = time_entries
      .map(entry => {
        const interval = formatClockifyTimeInterval(entry);
        totalDurationSec += interval.duration_in_seconds;
        return {
          id: interval.id,
          "Description": interval.description,
          "Start": interval.time_formatted.start,
          "End": interval.time_formatted.end,
          "Duration": interval.time_formatted.duration
        }
      });
    return { rows, total_duration: totalDurationSec };
  }

  const renderHoursSection = (submission_hours) => {
    const { rows, total_duration } = getFormattedRowsOfHours(submission_hours);
    const duration_in_hours = total_duration / 3600;
    return (
      <>
        <StyledDataTable 
          columns={["Description", "Start", "End", "Duration"]}
          rows={rows}
        />
        <Divider sx={{ borderBottomWidth: 'medium' }} />
        <Grid container justifyContent='flex-start'>
            <Grid item xs={12} md={6}>
                <StyledLineItem primary={"Total Hours"} secondary={(duration_in_hours).toFixed(1)} sx={{ padding: '8px 16px 8px 16px' }} />
            </Grid>
        </Grid>
      </>
    )
  }

  const canUseTimer = () => {
    const userId = getUserId();
    const teamMember = teamMembers && teamMembers.find((t) => t.user_id === userId);
    if (!teamMember || !teamMember.clockify || !teamMember.clockify.clockify_api_key || !teamMember.clockify.clockify_user_id) return false;
    return isAdmin();
  }



  const renderClockifyTimer = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {!isTimerRunning ? (
          <ContainedButton
            variant="contained"
            startIcon={<PlayArrowOutlined />}
            disabled={!!timer}
            onClick={handleStartTimer}
            sx={{
              borderRadius: '999px',
              fontWeight: 700,
              px: 2.25,
              py: 0.75,
              transition: 'all 0.25s ease',
              transform: 'scale(1)',
              '&:hover': {
                transform: 'scale(1.04)',
              },
            }}
          >
            Start
          </ContainedButton>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              animation: 'timerPulse 0.35s ease-out',
              '@keyframes timerPulse': {
                '0%': {
                  transform: 'scale(0.9)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            }}
          >
            <IconButton
              onClick={handlePauseTimer}
              sx={{
                width: 42,
                height: 42,
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                transition: 'all 0.25s ease',
                '&:hover': {
                  bgcolor: 'warning.dark',
                  transform: 'scale(1.08)',
                },
              }}
            >
              <StopCircle />
            </IconButton>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'success.main',
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                Active Timer
              </Typography>

              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formattedElapsedTime}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    )
  }



  return (
    <>
      <StyledDialog
        fullWidth
        open={open && !confirmCancelRequest && !showEditRequestModal && !showTimerSummaryDialog}
        maxWidth="md"
        sx={{
          '& > .MuiDialog-container > .MuiPaper-root': {
            border: isTimerRunning ? '1px solid' : '1px solid transparent',
            borderColor: isTimerRunning ? 'warning.main' : 'transparent',
            transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
            boxShadow: isTimerRunning
              ? '0 0 0 3px rgba(237, 108, 2, 0.12)'
              : undefined,
          },
        }}
      >
          <DialogTitle>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Box>
                <h4 style={{ margin: 0, padding: 0 }}>{SUBMISSION_TYPES_HUMAN_MAP[submission_type] || submission_type}</h4>
                <h6 style={{ margin: 0, padding: 0, fontSize: 18, fontWeight: 'normal' }}>Reference: <strong>{submission_id ? submission_id.substring(submission_id.length - 6).toUpperCase() : 'N/A'}</strong></h6>
                {isAdmin() && <h6 style={{ margin: 0, padding: 0, fontSize: 18, fontWeight: 'normal' }}>Assigned To: <strong>{getTeamMemberFormattedName(teamMembers, submission_assigned_to) || 'Unassigned'}</strong></h6>}
                <h6 style={{ margin: 0, padding: 0, fontSize: 18, fontWeight: 'normal' }}><em>Received on {formatDateTimeToFancyString(submission_received_epoch)}</em></h6>
              </Box>
              <Box>
                {canUseTimer() && renderClockifyTimer()}
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              <h6 style={{ margin: 0, padding: 0, fontSize: 20 }}>Provided Information:</h6>
              {submission_identifiers && submission_identifiers.site &&
              <>
                <StyledLineItem primary={"Account"} secondary={<><Link href={`/admin/sites/${submission_identifiers.site.site_id}/manage`} target="_blank" underline="hover">{submission_identifiers.site.site_name}</Link><LaunchOutlined sx={{ fontSize: '16px', marginBottom: '-4px', marginLeft: '5px'}} /></>} sx={{ padding: '8px 16px 8px 16px' }} />
                <Divider />
              </>
              }
              {submission_fields &&
                <>
                  <StyledLineItem primary={"Title"} secondary={submission_fields["title"]} sx={{ padding: '8px 16px 8px 16px' }} />
                  <Divider />
                  <StyledLineItem primary={"Status"} secondary={submission_status ? SUBMISSION_STATUS_LOOKUP[submission_status].label : 'N/A'} sx={{ padding: '8px 16px 8px 16px' }} />
                  <Divider />
                  <StyledLineItem primary={"Description"} sx={{ padding: '8px 16px 0px 16px' }} />
                  <div style={{ padding: '8px 16px 8px 16px' }}
                    dangerouslySetInnerHTML={{ __html: submission_fields["description"] }}
                  />
                  <Divider />
                </>
              }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', marginTop: '4px' }}>
              <StyledAccordionItem
                title="Attachments"
                details={submission_attachments && submission_attachments.length > 0 ? submission_attachments.map(attachment => (
                  <Link href={attachment.document_s3_location} target="_blank">{attachment.document_file_name}</Link>
                  )) : 'No attachments provided.'}
                titleStyling={SUMMARY_TITLE_FONT}
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', marginTop: '4px' }}>
              <StyledAccordionItem
                title={`Recorded Work (${submission_hours && submission_hours.length} Entries)`}
                details={submission_hours && submission_hours.length > 0 ? renderHoursSection(submission_hours) : 'No work has been recorded for this request.'}
                titleStyling={SUMMARY_TITLE_FONT}
              />
            </Box>
          </DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '16px'}}>
            {!isClosedSubmission() ? <TextButton
              sx={{ marginRight: '10px', color: Colors.redColor }}
              onClick={() => setConfirmCancelRequest(true)}
              disabled={isLoading || (submission_status !== SUBMISSION_STATUS.SUBMITTED && !isUserAdmin())}
            >Cancel Request</TextButton>
            : <div></div>}
            <Box>
              {isAdmin() && !isClosedSubmission() &&
                <TextButton sx={{ marginRight: '10px' }} onClick={() => setShowEditRequestModal(true)} disabled={isLoading || (submission_status !== SUBMISSION_STATUS.SUBMITTED && !isUserAdmin())}>Edit</TextButton>
              }
              <LoadingButton onClick={handleClose} isLoading={isLoading}>Close</LoadingButton>
            </Box>
          </Box>
      </StyledDialog>
      <ConfirmationModal
        open={confirmCancelRequest}
        onCancel={() => setConfirmCancelRequest(false)}
        description={"Please confirm you would like to cancel this request. If you proceed, the updates or changes to your website or virtual assets will not be made."}
        okButtonText='Cancel Request'
        cancelButtonText='Go Back'
        onOk={handleCancelRequest}
      />
      <EditRequestModal
        open={showEditRequestModal}
        onClose={() => setShowEditRequestModal(false)}
        onSave={() => {
          setShowEditRequestModal(false);
          handleClose();
        }}
        request={request}
      />
      <AddTimeEntryModal
        open={showTimerSummaryDialog}
        onClose={handleCLoseTimerEntry}
        onSave={handleSaveTimeEntry}
        onDiscard={handleDiscardTimeEntry}
        timer={timeEntry.current}
      />
    </>
  )
}

export default observer(withRouter(ViewRequestModal));
// LEFT OFF: NEED TO PROVIDE ABILITY TO CANCEL REQUEST
// NEED TO BE ABLE TO EDIT SUBMITTED DRAFTS!! - Make Modal after submitted status to state that the need to contact us if they want to make a change! Admins can always edit!!
